import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { sourceLabel, deriveStatus, STATUS_BADGE_CLASS, fmtDateTime } from "../lib/requestDisplay";
import RequestTimeline from "../components/RequestTimeline";
import { deliveryTone } from "../lib/requestDelivery";
import { detailsLine, detailsPayload } from "../lib/appointmentDetails";
import CallbackContactEditor from "../components/CallbackContactEditor";
import { useAuth } from "../lib/auth";
import { actsFor } from "../lib/maintenance";
import { ConflictDialog } from "./QueueDialogs";
import { conflictDetail, conflictSummary, overridePayload } from "../lib/scheduleConflict";

export default function RequestDetailDialog({ taskId, open, onOpenChange, onChange }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [schedDate, setSchedDate] = useState("");
  const [schedLabel, setSchedLabel] = useState("");
  const [provider, setProvider] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [location, setLocation] = useState("");
  const [updateNote, setUpdateNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);

  const load = () => {
    if (!taskId) return;
    api.get(`/tasks/${taskId}/detail`).then(({ data: d }) => {
      setData(d);
      setSchedDate(d.task.requested_for_date || "");
      setSchedLabel(d.task.requested_for_time_label || "");
      setProvider(d.task.provider_name || ""); setDiscipline(d.task.discipline || ""); setLocation(d.task.location || "");
      setUpdateNote(d.task.notes || "");
    }).catch(() => toast.error("Could not load request"));
  };
  useEffect(() => { if (open) load(); }, [taskId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (action) => {
    try {
      await api.post(`/tasks/${taskId}/${action}`);
      toast.success(action.charAt(0).toUpperCase() + action.slice(1) + "d");
      load();
      onChange?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  // Resident-visible schedule, appointment details and update - the SAME fields Aria's
  // check_request_status reads (routes/resident_requests.py's
  // _resident_safe_view) and the resident Home screen's request cards show.
  // Saving here is the ONLY way staff sets a real planned visit window -
  // Aria is instructed to say "no scheduled time yet" until this is filled in.
  const saveUpdate = async (extra = {}) => {
    setSaving(true);
    const body = { requested_for_date: schedDate || null, requested_for_time_label: schedLabel || null,
      ...detailsPayload({ provider, discipline, location }), notes: updateNote, ...extra };
    try {
      await api.patch(`/tasks/${taskId}`, body);
      let note = "";
      if (schedDate && schedLabel && !extra.conflict_override) {
        try { const { data } = await api.get(`/tasks/${taskId}/conflicts`); note = conflictSummary(data); } catch { /* saved; no note */ }
      }
      toast.success("Saved — resident and Aria will see this update" + (note ? ` ${note}` : ""));
      setConflict(null); load(); onChange?.();
    } catch (err) {
      const d = conflictDetail(err);
      if (d) setConflict({ summary: conflictSummary(d) });
      else toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  if (!taskId) return null;
  const t = data?.task;
  const ds = t ? deriveStatus(t) : null;
  // The same authority rule the backend applies to every lifecycle step
  // (task_lifecycle.authority_for): admin/owner, the assignee, or someone who
  // acts for the request's department. Anyone else sees the plan read-only
  // instead of a form the server would refuse.
  const dept = t?.visibility_role || t?.category;
  const canEdit = !!t && (actsFor(user, dept) || (!!t.assigned_to && t.assigned_to === user?.user_id));
  const isOpen = !!t && t.status !== "completed" && t.status !== "skipped";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="request-detail-dialog">
        <DialogHeader><DialogTitle className="font-display">Request detail</DialogTitle></DialogHeader>
        {!data ? <div className="text-caos-mute text-sm">Loading…</div> : (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-4">
              <div><span className="text-caos-mute">Resident</span><div className="font-semibold text-caos-forest">{t.resident_name || "unknown"}{t.room ? ` (Rm ${t.room})` : ""}</div></div>
              <div><span className="text-caos-mute">Department</span><div className="font-semibold text-caos-forest uppercase text-xs tracking-wider">{t.category}</div></div>
              <div><span className="text-caos-mute">Requested at</span><div>{fmtDateTime(t.created_at)}</div></div>
              <div><span className="text-caos-mute">Requested by / source</span><div>{sourceLabel(t.source)}</div></div>
              {data.delivery && <div className="col-span-2"><span className="text-caos-mute">Department notification</span><div className={deliveryTone(data.delivery) === "warn" ? "text-caos-terracotta font-semibold" : ""} data-testid="request-delivery">{data.delivery.label}{data.delivery.to?.length ? ` (${data.delivery.to.join(", ")})` : ""}</div></div>}
              <div><span className="text-caos-mute">Priority</span><div className="uppercase text-xs">{t.priority}</div></div>
              <div><span className="text-caos-mute">Status</span><div><Badge className={STATUS_BADGE_CLASS[ds]}>{ds}</Badge></div></div>
              <div><span className="text-caos-mute">Assigned to</span><div>{t.assigned_name || <span className="italic text-caos-mute">unassigned</span>}</div></div>
              {t.requested_for_date && <div><span className="text-caos-mute">Requested for</span><div>{t.requested_for_date} {t.requested_for_time_label && `(${t.requested_for_time_label})`}</div></div>}
              {detailsLine(t) && <div className="col-span-2"><span className="text-caos-mute">Appointment details</span><div data-testid="request-details-line">{detailsLine(t)}</div></div>}
              {t.re_request_count > 0 && <div><span className="text-caos-mute">Asked again</span><div>{t.re_request_count}x</div></div>}
              {t.conversation_session_id && <div className="col-span-2"><span className="text-caos-mute">Conversation session</span><div className="font-mono text-xs text-caos-mute">{t.conversation_session_id}</div></div>}
            </div>
            <div className="mb-4">
              <div className="text-caos-mute text-xs uppercase tracking-widest mb-1">Request</div>
              <div className="text-sm">{t.description || t.title}</div>
            </div>

            {(dept === "administration" || t.callback_phone || t.callback_window) && (
              <CallbackContactEditor task={t} canEdit={canEdit && isOpen} onSaved={() => { load(); onChange?.(); }} />
            )}

            {/* Lifecycle actions follow the same authority rule as the plan form; a viewer who
                cannot act sees the state, not buttons the server would refuse. */}
            {canEdit && <div className="flex gap-2 mb-4">
              {!t.acknowledged_at && <Button size="sm" variant="outline" className="border-2" onClick={() => act("acknowledge")} data-testid="request-acknowledge-btn">Acknowledge</Button>}
              {t.status === "pending" && <Button size="sm" className="bg-caos-forest" onClick={() => act("start")} data-testid="request-start-btn">Start</Button>}
              {t.status !== "completed" && t.status !== "skipped" && <Button size="sm" variant="outline" className="border-2" onClick={() => act("complete")} data-testid="request-complete-btn">Complete</Button>}
            </div>}

            {isOpen && !canEdit && (
              <div className="mb-6 rounded-xl border border-caos-line p-3 text-sm" data-testid="request-plan-readonly">
                <div className="text-caos-mute text-xs uppercase tracking-widest mb-1">Plan</div>
                <div>{t.requested_for_date ? `${t.requested_for_time_label || "time not set"} · ${t.requested_for_date}` : "No planned time yet."}</div>
                {detailsLine(t) && <div>{detailsLine(t)}</div>}
                <div className="text-caos-mute mt-1">Only {dept ? `${dept} staff` : "the owning department"}, the assignee or an administrator can change this plan.</div>
              </div>
            )}
            {isOpen && canEdit && (
              <div className="mb-6 rounded-xl border border-caos-line p-3">
                <div className="text-caos-mute text-xs uppercase tracking-widest mb-2">Resident-visible schedule &amp; update</div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="request-sched-date" />
                  <Input placeholder="e.g. 2-4 PM" value={schedLabel} onChange={(e) => setSchedLabel(e.target.value)} data-testid="request-sched-label" />
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Input placeholder="With (provider or staff name, optional)" maxLength={120} value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="request-provider" />
                  <Input placeholder="Kind (e.g. physical therapy, optional)" maxLength={60} value={discipline} onChange={(e) => setDiscipline(e.target.value)} data-testid="request-discipline" />
                  <Input className="col-span-2" placeholder="Where (e.g. Therapy room, 2nd floor, optional)" maxLength={160} value={location} onChange={(e) => setLocation(e.target.value)} data-testid="request-location" />
                </div>
                <Textarea
                  placeholder="Update the resident will see, e.g. 'Waiting on a replacement part from the vendor.'"
                  value={updateNote}
                  onChange={(e) => setUpdateNote(e.target.value)}
                  className="mb-2"
                  data-testid="request-update-note"
                />
                <Button size="sm" className="bg-caos-forest" disabled={saving} onClick={() => saveUpdate()} data-testid="request-save-update-btn">
                  {saving ? "Saving…" : "Save — resident & Aria will see this"}
                </Button>
              </div>
            )}

            <ConflictDialog open={!!conflict} onOpenChange={(o) => { if (!o) setConflict(null); }} summary={conflict?.summary}
              onSaveAnyway={() => saveUpdate(overridePayload({}))} testId="request-conflict" />
            <div>
              <h3 className="font-display text-lg font-medium text-caos-forest mb-2">Timeline</h3>
              <RequestTimeline task={t} receipts={data.receipts} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
